import { useEffect, useRef } from 'react';

import {
  CountdownReloadToast,
  usePreventAutoReloadOnNewVersion as usePreventAutoReload,
} from '@eluve/blocks';
import { useToast } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { useCaptureEvent } from '@eluve/posthog-react';

const NEW_VERSION_TOAST_ID = 'new-version-toast';

const NEW_VERSION_EVENT_NAMES = {
  viewed: 'user_viewed_new_version_toast',
  userDismissed: 'user_dismissed_new_version_toast',
  forceDismissed: 'force_dismissed_new_version_toast',
  userReloaded: 'user_reloaded_for_new_version',
  autoReloaded: 'auto_reloaded_for_new_version',
} as const;

type VersionCheckerProps = {
  children: React.ReactNode;
  intervalMinutes?: number;
};

export const VersionChecker: React.FC<VersionCheckerProps> = ({
  children,
  intervalMinutes = 5,
}) => {
  const logger = useNamedLogger('VersionChecker');
  const versionRef = useRef<string | null>(null);
  const toastOpenRef = useRef<boolean>(false);
  const captureEvent = useCaptureEvent();
  const toast = useToast();

  useEffect(() => {
    const checkVersion = async () => {
      const currentVersion = versionRef.current;
      const currentToastOpen = toastOpenRef.current;
      try {
        const headers = new Headers();
        if (currentVersion) {
          headers.append('If-None-Match', currentVersion);
        }
        const response = await fetch(window.location.href, {
          method: 'HEAD',
          headers,
        });

        const newVersion = response.headers.get('ETag');
        if (!newVersion) {
          return;
        }
        if (!currentVersion) {
          versionRef.current = newVersion;
          return;
        }

        const preventState = usePreventAutoReload.getState();
        if (preventState.shouldPrevent()) {
          logger.info(
            'Prevented prompting user to reload the page to get the latest updates',
            {
              newVersion,
              currentVersion,
              preventReasons: [...preventState.preventReasons],
            },
          );
          return;
        }

        if (newVersion !== currentVersion && !currentToastOpen) {
          logger.info(
            'Prompting user to reload the page to get the latest updates',
            { newVersion, currentVersion },
          );

          toastOpenRef.current = true;

          // No snooze on dismissal: a force-dismiss (a prevent-auto-reload
          // reason appearing mid-countdown) or a swipe just re-prompts on the
          // next check. Stale tabs that snoozed version updates recorded
          // sessions against deleted assets — silent total audio loss
          // (ENG-5284).
          const onDismiss = () => {
            toastOpenRef.current = false;
          };

          toast.message(
            <CountdownReloadToast
              toastId={NEW_VERSION_TOAST_ID}
              title="New Version Available"
              description="Eluve will reload to apply the latest version. Recording reliability depends on staying up to date."
              iconName="CircleFadingArrowUp"
              dismissible={false}
              eventNames={NEW_VERSION_EVENT_NAMES}
              meta={{ newAppVersion: newVersion }}
              captureEvent={captureEvent}
              onDismiss={onDismiss}
            />,
            {
              id: NEW_VERSION_TOAST_ID,
              position: 'bottom-right',
              duration: Infinity,
              onDismiss,
            },
          );
        }
      } catch (error) {
        logger.warn('Error checking version:', {
          error: (error as Error)?.message,
        });
      }
    };
    const intervalId = setInterval(checkVersion, intervalMinutes * 60 * 1000);
    return () => {
      clearInterval(intervalId);
    };
  }, [captureEvent, intervalMinutes, logger, toast]);

  return children;
};
